import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { Modal } from "bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "../../../style/custommillingHubdashboard.css";
import "../../../style/customresponsivefile.css";
import { CountdownTimer } from "../Designer/CountdownTimer";
import { useNavigate } from "react-router-dom";
import { ManagerOrderRow } from "../../../Interfaces/MangerOrderRow";
import STLViewerModal from "./STLViewerModal";
import SearchBox from "../../../Interfaces/SearchBoxProps";
import SortFilter from "../../CentralHub/Designer/SortFilter";

// Custom hooks and components
import { useDashboardData } from "./hooks/useDashboardData";
import { useOrderModal } from "./hooks/useOrderModal";
import { useTabActivity } from "./hooks/useTabActivity";
import { DashboardHeader } from "./components/DashboardHeader";
import { OrderDetailsModal } from "./components/OrderDetailsModal";
import { getStatusStyle } from "../../../utils/statusStyles";

// Import the ModalTab type from your hooks
import { apiAxios } from "../../../utils/apiAxios";
import AlertSoundManager from "../../../utils/AlertSoundManager";

// Constants
const API_PATH = process.env.REACT_APP_API_PATH_URL || "";
const REFRESH_INTERVAL = 60000; // 1 min

const STATUS_ITEMS = [
  { key: 'Active Orders', label: 'Active Orders', className: 'bgnone', icon: null, iconClass: null },
  { key: 'Awaiting Response', label: 'Awaiting Response', className: 'warning', icon: '../../awaiting.png', iconClass: null },
  { key: 'Needs Attention', label: 'Need Attention', className: 'danger', icon: '../../needsattantion2.png', iconClass: null },
  { key: 'On Time', label: 'On Time', className: 'bg-white-ontime', icon: '../../completed.png', iconClass: null },
  { key: 'Completed', label: 'Completed', className: 'success', icon: null, iconClass: 'bi bi-check-circle-fill completed-icon' },
  { key: 'Cancelled', label: 'Cancelled', className: 'cancelled', icon: null, iconClass: 'bi bi-dash-circle-fill' },
] as const;

const DashboardManagerCentralHub: React.FC = () => {
  const navigate = useNavigate();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedCancellation, setSelectedCancellation] = useState<any>(null);

  const [refundAmount, setRefundAmount] = useState("");
  const [isCustomRefund, setIsCustomRefund] = useState(false);
  const [orderView, setOrderView] = useState<"full" | "design">("full");
  const [selectedStatus, setSelectedStatus] = useState<string>("Active Orders");
  const isRefundExceeded = Number(refundAmount || 0) > Number(selectedCancellation?.paidAmount || 0);
  // State for auto-refresh that persists across page reloads
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {

    const saved = localStorage.getItem('autoRefreshEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  // Custom hooks
  const {
    dashboardCounts,
    loading,
    searchText,
    setSearchText,
    filteredOrders,
    refreshData,
    lastRefreshTime,
    changeStatusFilter
  } = useDashboardData();

  
 const {
  activeModalTab,
  orderDetails,
  detailsLoading,
  orderReview,
  isOrderModalOpen,
  selectedOrder,
  cancelReason,
  setCancelReason,
  closeModal,
  handleTabClick,
  openOrderModal,
  reassignDesigner,
  reassignMilling,
  reassignDelivery,
  cancelOrder,
  designers,
  millings,
  deliveryPartners,
  closeModalAfterAction
} = useOrderModal();

  const { isTabActive } = useTabActivity();

  // State for STL viewer
  const [showStlModal, setShowStlModal] = useState(false);
  const [selectedStl, setSelectedStl] = useState<string | null>(null);

  // Use refs to track current values for the interval callback
  const isTabActiveRef = useRef(isTabActive);
  const isOrderModalOpenRef = useRef(isOrderModalOpen);
  const showStlModalRef = useRef(showStlModal);
  const autoRefreshEnabledRef = useRef(autoRefreshEnabled);
  const refreshDataRef = useRef(refreshData);
  const [refundLoading, setRefundLoading] = useState(false);
  // Store interval ID in ref to prevent recreation
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update localStorage and ref when autoRefreshEnabled changes
  useEffect(() => {
    localStorage.setItem('autoRefreshEnabled', JSON.stringify(autoRefreshEnabled));
    autoRefreshEnabledRef.current = autoRefreshEnabled;
  }, [autoRefreshEnabled]);

  // Update refs when values change
  useEffect(() => {
    isTabActiveRef.current = isTabActive;
  }, [isTabActive]);

  useEffect(() => {
    isOrderModalOpenRef.current = isOrderModalOpen;
  }, [isOrderModalOpen]);

  useEffect(() => {
    showStlModalRef.current = showStlModal;
  }, [showStlModal]);

  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);

  // Create wrapped handlers that refresh data after actions
  const handleReassignDesigner = useCallback(
    async (
      caseId: number,
      designerId: number
    ): Promise<boolean> => {
      try {
        const success = await reassignDesigner(
          caseId,
          designerId
        );

        if (success) {
          await refreshData(true);
        }

        return success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [reassignDesigner, refreshData]
  );
  const handleReassignMilling = useCallback(
    async (
      caseId: number,
      millingId: number
    ): Promise<boolean> => {
      try {
        const success = await reassignMilling(
          caseId,
          millingId
        );

        if (success) {
          await refreshData(true);
        }

        return success;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [reassignMilling, refreshData]
  );
  const [Reason, setReason] = useState("");


  const handleCancelOrder = useCallback(async (caseId: number, reason: string) => {
    try {

      const currentOrder = filteredOrders.find(x => x.caseID === caseId);
  
      if (
        currentOrder &&
        currentOrder.DesignerStatus?.toLowerCase() === "done" &&
        currentOrder.MillingStatus?.toLowerCase() === "done"
      ) {
        toast.warning(
          "This order has already been completed by Designer and Milling. It cannot be cancelled."
        );
        return;
      }
  
      const token = sessionStorage.getItem("token") || "";
      setReason(reason);
  
      const res = await apiAxios.get(
        `/Dentist/refund-details/${caseId}`,
        {
          headers: {
            AuthorizationData: token
          }
        }
      );
  
      if (res.data?.isSuccess) {
        const item = res.data.singleData;
  
        setSelectedCancellation(item);
        setRefundAmount(String(item.suggestedRefundAmount || 0));
        setIsCustomRefund(false);
        setShowRefundModal(true);
      } else {
        toast.error(
          res.data?.message || "Unable to load refund details"
        );
      }
  
    } catch (error) {
      console.error(error);
      toast.error("Unable to load refund details");
    }
  }, [filteredOrders]);

  const scans = useMemo(() => {
    if (!orderReview.length) return [];
    const review = orderReview[0];
    if (!review?.filePaths) return [];

    const paths = review.filePaths
      .split(",")
      .map((path: string) => path.trim())
      .filter(Boolean);

    const names = review.fileNames
      ? review.fileNames.split(",").map((name: string) => name.trim())
      : [];

    return paths.map((path: string, index: number) => ({
      path,
      name: names[index] || path.split("/").pop() || `Scan ${index + 1}`
    }));
  }, [orderReview]);

  const isDesignAcceptanceOverdue = useMemo(
    () => selectedOrder?.DesignerStatus?.toLowerCase() === "acceptance overdue",
    [selectedOrder]
  );

  const isMillingAcceptanceOverdue = useMemo(
    () => selectedOrder?.MillingStatus?.toLowerCase() === "acceptance overdue",
    [selectedOrder]
  );

  const isDesignerRejected = useMemo(
    () => selectedOrder?.DesignerStatus?.toLowerCase() === "rejected",
    [selectedOrder]
  );

  const isMillingRejected = useMemo(
    () => selectedOrder?.MillingStatus?.toLowerCase() === "rejected",
    [selectedOrder]
  );

 
  const approveRefund = async () => {
  if (refundLoading) return;

  const refund = Number(refundAmount);

  // Validation first
  if (selectedCancellation.paidAmount > 0) {
    if (refund < 1 || refund > selectedCancellation.paidAmount) {
      toast.error(
        `Refund Amount must be between 1 and ₹${selectedCancellation.paidAmount}`
      );
      return;
    }
  } else {
    if (refund !== 0) {
      toast.error("Refund Amount must be 0.");
      return;
    }
  }

  try {
    setRefundLoading(true);

    const token = sessionStorage.getItem("token") || "";

    const payload = {
      caseID: selectedCancellation.caseID,
      refundAmount: refund,
      isCustomRefund,
      cancellationReason: Reason,
    };

    const res = await apiAxios.post(
      `/Dentist/cancel-approve`,
      payload,
      {
        headers: {
          AuthorizationData: token,
          "Content-Type": "application/json",
        },
      }
    );

    if (res.data.isSuccess) {
      setShowRefundModal(false);
      closeModalAfterAction();

      toast.success("Cancellation approved");

      refreshData(true);
    } else {
      toast.error(res.data?.message || "Refund failed");
    }
  } catch (error) {
    console.error("Refund error:", error);
    toast.error("Refund failed");
  } finally {
    setRefundLoading(false);
  }
};

  // Effects
  useEffect(() => {
    refreshData(true);
  }, [refreshData]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval - use refs to get current values
    intervalRef.current = setInterval(() => {
      const now = new Date().toLocaleTimeString();

      // Get current values from refs
      const currentIsTabActive = isTabActiveRef.current;
      const currentIsOrderModalOpen = isOrderModalOpenRef.current;
      const currentShowStlModal = showStlModalRef.current;
      const currentAutoRefreshEnabled = autoRefreshEnabledRef.current;
      const currentRefreshData = refreshDataRef.current;



      if (currentAutoRefreshEnabled && currentIsTabActive && !currentIsOrderModalOpen && !currentShowStlModal) {
        currentRefreshData(false);
      } else {
      }
    }, REFRESH_INTERVAL);


    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);


  // Handlers
  const handleStlView = (scan: any): void => {
    if (!scan.path) {
      toast.error("STL not available");
      return;
    }

    navigate("/manager/ManagerstlPreview", {
      state: {
        stlUrl: `${API_PATH}${scan.path}`
      }
    });
  };

  const [hasSOS, setHasSOS] = useState(false);

  const [audioEnabled, setAudioEnabled] = useState(false);
  const enableAudio = async () => {
    try {
      await AlertSoundManager.unlock();

      setAudioEnabled(true);
      localStorage.setItem("audioEnabled", "true");

      toast.success("Sound alerts enabled.");
    } catch {
      toast.error("Please allow sound playback.");
    }
  };




  useEffect(() => {
    if (!audioEnabled) return;

    if (hasSOS) {
      AlertSoundManager.play();
    } else {
      AlertSoundManager.stop();
    }
  }, [hasSOS, audioEnabled]);

  useEffect(() => {
    const sos = filteredOrders.some(
      x => x.status === "Needs Attention"
    );

    setHasSOS(sos);
  }, [filteredOrders]);

  // Toggle auto-refresh function
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled((prev: any) => !prev);
    toast.info(`Auto-refresh ${!autoRefreshEnabled ? 'enabled' : 'disabled'}`);
  }, [autoRefreshEnabled]);

  // const displayedOrders = useMemo(() => {
  //   return filteredOrders.filter(order =>
  //     orderView === "design"
  //       ? order.type === "Design Service"
  //       : order.type !== "Design Service"
  //   );
  // }, [filteredOrders, orderView]);
const displayedOrders = useMemo(() => {
  return filteredOrders.filter((order) => {

    // ==============================
    // COMPLETED TAB
    // ==============================
    if (selectedStatus === "Completed") {
      return order.status === "Completed";
    }

    // ==============================
    // CANCELLED TAB
    // ==============================
    if (selectedStatus === "Cancelled") {
      return order.status === "Cancelled";
    }
    // NEEDS ATTENTION TAB
if (selectedStatus === "Needs Attention") {
  return (
    order.status === "Needs Attention" ||
    order.status === "Needs Attention(Pickup Overdue)"
  );
}

    // ==========================================
    // ALL OTHER TABS
    // Never show Completed / Cancelled
    // ==========================================
    if (
      order.status === "Completed" ||
      order.status === "Cancelled"
    ) {
      return false;
    }

    // ==========================================
    // SERVICE TYPE FILTER
    // ==========================================
    return orderView === "design"
      ? order.type === "Design Service"
      : order.type !== "Design Service";
  });
}, [filteredOrders, orderView, selectedStatus]);

  // Table Columns
  const columns = useMemo(() => [
    {
      name: "Order ID",
      selector: (row: ManagerOrderRow) => row.orderId,
      minWidth: "100px",
      allowOverflow: true,   // ✅ IMPORTANT
      button: false
    },
    {
      name: "Type",
      selector: (row: ManagerOrderRow) => row.type,
      ignoreRowClick: false,
      minWidth: "100px",
    },
    {
      name: "Expected Delivery",
      selector: (row: ManagerOrderRow) => row.expectedDelivery,
      //cell: (row: ManagerOrderRow) => <span>{row.expectedDelivery}</span>,
      cell: (row: ManagerOrderRow) => (
        <div onClick={() => openOrderModal(row)}>
          {row.expectedDelivery}
        </div>
      ),
      sortable: true,
      minWidth: "180px",

    },
    ...(orderView === "full"
      ? [{
        name: "Assigned Milling Hub",
        selector: (row: ManagerOrderRow) => row.assignedHub ?? "N/A",
        cell: (row: ManagerOrderRow) => (
          <div onClick={() => openOrderModal(row)}>
            {row.assignedHub || "N/A"}
          </div>
        ),
        sortable: true,
        minWidth: "180px",
      }]
      : []),
    {
      name: "Assigned Designer",
      selector: (row: ManagerOrderRow) => row.assignedDesigner ?? "N/A",
      //cell: (row: ManagerOrderRow) => <span>{row.assignedDesigner || "N/A"}</span>,
      cell: (row: ManagerOrderRow) => (
        <div

          onClick={() => openOrderModal(row)}  // ✅ force click
        >
          {row.assignedDesigner || "N/A"}
        </div>
      ),
      sortable: true,
      minWidth: "170px",

    },
    ...(orderView === "full"
      ? [{
        name: "Milling",
        cell: (row: ManagerOrderRow) => (
          <div onClick={() => openOrderModal(row)}>
            {row.status === "Awaiting Response" && (
              <CountdownTimer
  dueOn={row.millingAcceptanceDueOn}
  createdOn={row.createdOn}
  status="Awaiting Response"
/>
            )}
            <span style={{ marginLeft: 6 }}>
              {row.MillingStatus}
            </span>
          </div>
        ),
        minWidth: "180px",
      }]
      : []),
    {
      name: "Design",
      cell: (row: ManagerOrderRow) => (
        <div onClick={() => openOrderModal(row)}>
          {row.status === "Awaiting Response" && (
            <CountdownTimer
  dueOn={row.designerAcceptanceDueOn}
  createdOn={row.createdOn}
  status="Awaiting Response"
/>
          )}
          <span style={{ marginLeft: 6 }}>{row.DesignerStatus}</span>
        </div>
      ),
      minWidth: "180px",
    },


    {
      name: "Status",
      minWidth: "170px",
      cell: (row: ManagerOrderRow) => {
        const item = STATUS_ITEMS.find(s => s.key === row.status);

        const isNeedsAttention = row.status === "Needs Attention";

        return (
          <span
            style={{
              ...getStatusStyle(row.status),
              padding: "6px 8px",
              borderRadius: "5px",
              fontSize: "13px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontWeight: 400,
              textAlign: "center",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className={`cursor-pointer ${isNeedsAttention ? "blink-status" : ""}`}
            onClick={() => openOrderModal(row)}
          >
            {item?.icon ? (
              <img src={item.icon} alt="" className="status-icon" />
            ) : (
              item?.iconClass && <i className={item.iconClass}></i>
            )}
            {row.status}
          </span>
        );
      },
    }
  ], [openOrderModal, orderView]);

  return (
    <>
      {!audioEnabled && (
        <div className="audio-permission-overlay">
          <div className="audio-permission-box">
            <div className="audio-icon">🔔</div>

            <p>
              Click below to allow emergency sound alerts for
              <strong> Needs Attention</strong> orders.
            </p>

            <button
              className="btn btn-danger enable-sound-btn"
              onClick={enableAudio}
            >
              Enable Sound Alerts
            </button>
          </div>
        </div>
      )}

      {/* Debug Header */}
      <div className="refresh-container rounded pb-1 mb-3">
        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
          {/* RIGHT BUTTONS */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm px-3"
              onClick={() => {
                refreshData(true);
              }}
              disabled={loading}
              title={loading ? "Refreshing..." : "Refresh Data"}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh
                </>
              )}
            </button>

            <button
              className={`btn btn-sm px-3 ${autoRefreshEnabled ? 'btn-success' : 'btn-secondary'}`}
              onClick={toggleAutoRefresh}
              title={autoRefreshEnabled ? "On" : "Off"}
            >
              <i className={`bi ${autoRefreshEnabled ? "bi bi-broadcast me-1" : "bi bi-broadcast me-1"} me-1`}></i>
              {autoRefreshEnabled ? 'On' : 'Off'}
            </button>

            <button
              className={`btn btn-sm px-3 text-white ${autoRefreshEnabled ? 'bg-info' : 'bg-danger'}`}
              title={autoRefreshEnabled ? "Auto  ON" : "Auto Off"}
            >
              <i className={`bi ${autoRefreshEnabled ? "bi-arrow-repeat" : "bi-stop-circle-fill"} me-1`}></i>
              Auto: {autoRefreshEnabled ? 'On' : 'Off'}
            </button>
          </div>
        </div>


        {/* Status Bar */}
        {/* <DashboardHeader counts={dashboardCounts} /> */}
        <div className={`main-header-section-top ${hasSOS ? "dashboard-sos" : ""}`}>
      
         <DashboardHeader
  counts={dashboardCounts}
  onStatusClick={(status) => {
    setSelectedStatus(status!);

    // Don't filter from backend for Needs Attention.
    changeStatusFilter(
      status === "Needs Attention" ? "Active Orders" : status
    );
  }}
/>
        </div>
        {/* Table Section */}
        <div className="shadow-container rounded p-3 mt-4">
          <div className="mb-3 d-flex gap-4">
            <label>
              <input
                type="radio"
                checked={orderView === "full"}
                onChange={() => setOrderView("full")}
              />
              {" "}Full Service
            </label>

            <label>
              <input
                type="radio"
                checked={orderView === "design"}
                onChange={() => setOrderView("design")}
              />
              {" "}Design Service
            </label>
          </div>
          <div className="search-button-section mb-3">
            <SearchBox value={searchText} onChange={setSearchText} />
            <SortFilter />
          </div>

          {filteredOrders.length === 0 && !loading ? (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              No orders found. {searchText ? "Try a different search." : "Data might not be loaded yet."}
              <button
                className="btn btn-sm btn-primary ms-2"
                onClick={() => {
                  refreshData(true);
                }}
              >
                Load Data
              </button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={displayedOrders}
              progressPending={loading}
              onRowClicked={(row) => openOrderModal(row)}
              progressComponent={
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading orders...</p>
                </div>
              }
              pagination
              highlightOnHover
              responsive
              selectableRows={false}
              pointerOnHover
              noDataComponent={
                <div className="text-center p-4">
                  <i className="bi bi-inbox display-4 text-muted"></i>
                  <p className="mt-2">No orders available</p>
                  <button
                    className="btn btn-primary mt-2"
                    onClick={() => {
                      refreshData(true);
                    }}
                  >
                    Load Orders
                  </button>
                </div>
              }
            />
          )}
        </div>

        {/* Order Details Modal */}
        <OrderDetailsModal
          isOpen={isOrderModalOpen}
          orderDetails={orderDetails}
          activeTab={activeModalTab}
          loading={detailsLoading}
          orderReview={orderReview}
          scans={scans}
          cancelReason={cancelReason}
          isDesignAcceptanceOverdue={isDesignAcceptanceOverdue}
          isMillingAcceptanceOverdue={isMillingAcceptanceOverdue}
          isDesignerRejected={isDesignerRejected}
          isMillingRejected={isMillingRejected}
          selectedOrder={selectedOrder}
          onClose={closeModal}
          designers={designers}
millings={millings}
deliveryPartners={deliveryPartners}
onTabChange={handleTabClick}
          onViewStl={handleStlView}
          onReassignDesigner={handleReassignDesigner}
onReassignMilling={handleReassignMilling}
onReassignDelivery={reassignDelivery}
onCancelOrder={handleCancelOrder}
          onCancelReasonChange={setCancelReason}
        />

        {/* STL Viewer Modal */}
        {showStlModal && (
          <>
            <div className="modal-backdrop-stl-viewer fade show"></div>
            <STLViewerModal
              selectedStl={selectedStl}
              setShowStlModal={() => {
                setShowStlModal(false);
                const orderModalEl = document.getElementById("orderDetailsModal");
                if (orderModalEl) {
                  new Modal(orderModalEl).show();
                }
              }}
            />
          </>
        )}

        {showRefundModal &&
          selectedCancellation && (
            <div className="modal-overlay">
              <div className="custom-modal">

                <div className="modal-header manager-modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title mb-0">
                    Approve Cancellation
                  </h4>

                  <button
                    type="button"
                    className="custom-close-btn3"
                    aria-label="Close"
                    onClick={() =>
                      setShowRefundModal(
                        false
                      )
                    }
                  >
                    <i className="bi bi-x customcross"></i>
                  </button>
                </div>
                <ul className="list-unstyled refund-summary-box mb-0">

                  <li className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span>Paid Amount</span>
                    <strong>₹{selectedCancellation?.paidAmount}</strong>
                  </li>

                  <li className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span>Deduction</span>
                    <strong className="text-danger">
                      ₹{selectedCancellation?.deductionAmount}
                    </strong>
                  </li>

                  <li className="d-flex justify-content-between align-items-center py-2">
                    <span>Suggested Refund</span>
                    <strong className="text-success">
                      ₹{selectedCancellation?.suggestedRefundAmount}
                    </strong>
                  </li>
                </ul>

                {selectedCancellation
                  .allowCustomRefund && (
                    <>
                      <div className="d-flex">
                        <input
                          type="checkbox"
                          checked={
                            isCustomRefund
                          }
                          onChange={(e) =>
                            setIsCustomRefund(
                              e.target.checked
                            )
                          }
                        />

                        <div className="mx-1" >Custom Refund</div> </div>
                    </>
                  )}

                <input
                  type="number"
                  className="form-control mt-3"
                  disabled={!isCustomRefund}
                  min={selectedCancellation.paidAmount > 0 ? 1 : 0}
                  max={selectedCancellation.paidAmount}
                  value={refundAmount}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "") {
                      setRefundAmount("");
                      return;
                    }

                    const amount = Number(value);

                    if (selectedCancellation.paidAmount > 0) {
                      if (amount < 1) {
                        setRefundAmount("1");
                      } else if (amount > selectedCancellation.paidAmount) {
                        setRefundAmount(String(selectedCancellation.paidAmount));
                      } else {
                        setRefundAmount(value);
                      }
                    } else {
                      setRefundAmount("0");
                    }
                  }}
                />
                {isRefundExceeded && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "5px"
                    }}
                  >
                    Refund Amount cannot be greater than Paid Amount
                    (₹{selectedCancellation?.paidAmount})
                  </div>
                )}
                <div className="mt-3 text-end">

                  <button
                    className="btn btn-secondary me-2"
                    onClick={() =>
                      setShowRefundModal(
                        false
                      )
                    }
                  >
                    Close
                  </button>

                  {!isRefundExceeded && (
                    <button
  type="button"
  className="btn btn-primary"
  onClick={approveRefund}
  disabled={refundLoading}
>
  {refundLoading ? (
    <>
      <span
        className="spinner-border spinner-border-sm me-2"
        role="status"
        aria-hidden="true"
      />
      Processing...
    </>
  ) : (
    "Approve"
  )}
</button>
                  )}

                </div>

              </div>
            </div>
          )}

      </div>
    </>
  );
};

export default DashboardManagerCentralHub;